import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { parseUntrackedCsv, UNTRACKED_CSV_COLUMNS, type ParsedCsvResult } from '../utils/csvUpload';
import { uploadUntrackedCsv, fetchUntrackedData, type UploadUntrackedResult, type UploadUntrackedMode } from '../utils/database';
import { transformRequestResponse } from '../utils/transformers';
interface Props {
  onClose: () => void;
  onUploaded: () => void;
}
const PREVIEW_ROW_CAP = 200;
export function UploadCsvModal({
  onClose,
  onUploaded
}: Props) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedCsvResult | null>(null);
  const [mode, setMode] = useState<UploadUntrackedMode>('append');
  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadUntrackedResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  const invalidCount = parsed ? parsed.rows.filter(r => r.issues.length > 0).length : 0;
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploadResult(null);
    setUploadError(null);
    setIsParsing(true);
    try {
      const result = await parseUntrackedCsv(file);
      setParsed(result);
    } finally {
      setIsParsing(false);
    }
  }
  function handleClear() {
    setFileName(null);
    setParsed(null);
    setMode('append');
    setUploadResult(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }
  async function handleUpload() {
    if (!parsed) return;
    if (mode === 'replace') {
      const ok = window.confirm('Replace mode deletes ALL existing untracked rows before inserting this upload. This cannot be undone. Continue?');
      if (!ok) return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      const rows = parsed.rows.map(r => r.raw);
      const result = await uploadUntrackedCsv(rows, mode);
      setUploadResult(result);
      onUploaded();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }
  async function handleDownload() {
    setDownloadError(null);
    setIsDownloading(true);
    try {
      const rows = await fetchUntrackedData();
      const csv = Papa.unparse(transformRequestResponse(rows), {
        columns: [...UNTRACKED_CSV_COLUMNS]
      });
      const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wf_untracked_data_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  }
  const previewRows = parsed ? parsed.rows.slice(0, PREVIEW_ROW_CAP) : [];
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-4 px-2" style={{
    background: 'rgba(0,0,0,0.55)'
  }} onClick={onClose} data-id="element-1203" data-mp="11re3exewpzvj">
      <div className="relative bg-white rounded-xl shadow-2xl w-full" style={{
      maxWidth: '98vw'
    }} onClick={e => e.stopPropagation()} data-id="element-1204" data-mp="33mesvw4cdju6">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200" data-id="element-1205" data-mp="12uv09zcr4hda">
          <div data-id="element-1206" data-mp="xxcps4j35u36">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-800" data-id="element-1207" data-mp="xvru2bl1c53q">Upload untracked requests (CSV)</h2>
            <p className="mt-1 text-xs text-slate-500" data-id="element-1208" data-mp="1bvjfgxdvsnrr">
              Columns expected: {UNTRACKED_CSV_COLUMNS.join(', ')}. Dates must be dd/MM/yy.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-base leading-none font-bold" aria-label="Close" data-id="element-1209" data-mp="k5y5ludfzmuj">
            ✕
          </button>
        </div>

        <div className="px-6 py-4" data-id="element-1210" data-mp="szkztg204i1m">
          <div className="flex items-center justify-between mb-3" data-id="element-1211" data-mp="o68bz855cf9x">
            <button onClick={() => void handleDownload()} disabled={isDownloading} className="px-3 py-1 rounded-md border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors" data-id="element-1212" data-mp="o4x878bpb7it">
              {isDownloading ? 'Downloading…' : 'Download current data (CSV)'}
            </button>
          </div>
          {downloadError && <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-id="element-1213" data-mp="38jdj6vo67qgr">
              {downloadError}
            </div>}

          {!parsed && <div className="flex items-center gap-3" data-id="element-1214" data-mp="51lnp7atsodt">
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={e => void handleFileChange(e)} className="text-xs" data-id="element-1215" data-mp="52wsnag0c26i" />
              {isParsing && <span className="text-xs text-slate-500" data-id="element-1216" data-mp="2iq27x6dkfv">Parsing…</span>}
            </div>}

          {parsed && <>
              <div className="flex items-center justify-between mb-3" data-id="element-1217" data-mp="2fsmxrn8k1rkr">
                <div className="text-xs text-slate-600" data-id="element-1218" data-mp="2kuc6uxc7vv67">
                  <strong data-id="element-1219" data-mp="rakbvykckplm">{fileName}</strong> — {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} parsed
                  {invalidCount > 0 && <span className="text-red-600 font-medium" data-id="element-1220" data-mp="mbzb9y16o3wn"> · {invalidCount} row{invalidCount === 1 ? '' : 's'} with issues (highlighted below)</span>}
                  {parsed.rows.length > PREVIEW_ROW_CAP && <span data-id="element-1221" data-mp="cjthcirdfcet"> · showing first {PREVIEW_ROW_CAP} rows</span>}
                </div>
                <div className="flex items-center gap-3" data-id="element-1222" data-mp="3l7sy6z7x364">
                  <div className="flex items-center gap-3 text-xs text-slate-600" data-id="element-1223" data-mp="3jobwe5qmo3a">
                    <label className="flex items-center gap-1 cursor-pointer" data-id="element-1224" data-mp="3us27n7mui65o">
                      <input type="radio" name="upload-mode" checked={mode === 'append'} onChange={() => setMode('append')} data-id="element-1225" data-mp="3pszeolk53qe2" />
                      Append
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-id="element-1226" data-mp="1o0b5zwoq8aqw">
                      <input type="radio" name="upload-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} data-id="element-1227" data-mp="1vkc0g6t7eorz" />
                      Replace existing
                    </label>
                  </div>
                  <button onClick={handleClear} className="px-3 py-1 rounded-md border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-50 transition-colors" data-id="element-1228" data-mp="25aezann0awy8">
                    Clear / re-upload CSV
                  </button>
                  <button onClick={() => void handleUpload()} disabled={isUploading || parsed.rows.length === 0} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors" data-id="element-1229" data-mp="1hk1972nzf86a">
                    {isUploading ? 'Uploading…' : mode === 'replace' ? 'Replace existing data' : 'Upload to dashboard'}
                  </button>
                </div>
              </div>

              {mode === 'replace' && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-id="element-1230" data-mp="3nzvf0bh14zjb">
                  ⚠ Replace mode will delete all previously uploaded untracked rows before inserting this file.
                </div>}

              {parsed.fileErrors.length > 0 && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-id="element-1231" data-mp="an1o59itfz5c">
                  {parsed.fileErrors.map((e, i) => <div key={i} data-id="element-1232" data-mp="6konv0mu8tfy" data-mpi={i}>{e}</div>)}
                </div>}

              {uploadError && <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-id="element-1233" data-mp="kkg65dke0vzr">
                  {uploadError}
                </div>}

              {uploadResult && <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-id="element-1234" data-mp="2meffvuhv35ac">
                  ✓ Uploaded ({uploadResult.mode}) — {uploadResult.inserted} row{uploadResult.inserted === 1 ? '' : 's'} inserted
                  {uploadResult.skippedCount > 0 && `, ${uploadResult.skippedCount} skipped`}.
                  {uploadResult.skipped.length > 0 && <ul className="mt-1 list-disc list-inside" data-id="element-1235" data-mp="s4qsxhxdtt9p">
                      {uploadResult.skipped.slice(0, 20).map((s, i) => <li key={i} data-id="element-1236" data-mp="e58x4ilo5fqu" data-mpi={i}>{s}</li>)}
                    </ul>}
                </div>}

              <div style={{
            overflowX: 'auto',
            maxHeight: '55vh',
            overflowY: 'auto'
          }} className="border border-slate-200 rounded-md" data-id="element-1237" data-mp="j53oen9cw7cf">
                <table className="tbl tbl-scroll" data-id="element-1238" data-mp="48mhc8879evo">
                  <thead data-id="element-1239" data-mp="9532quo0lf52">
                    <tr data-id="element-1240" data-mp="96ehtqmwu9bh">
                      {UNTRACKED_CSV_COLUMNS.map(c => <th key={c} data-id="element-1241" data-mp="1yl2k2z1seavs">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-1242" data-mp="23jnjwxd4msjp">
                    {previewRows.map(row => <tr key={row.index} style={row.issues.length > 0 ? {
                  background: '#fef2f2'
                } : undefined} title={row.issues.join('; ')} data-id="element-1243" data-mp="23ht580sg7wo2">
                        {UNTRACKED_CSV_COLUMNS.map(c => <td key={c} style={{
                    fontSize: 10
                  }} data-id="element-1244" data-mp="u0t5qgny5dxw">{row.raw[c] ?? ''}</td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </>}
        </div>
      </div>
    </div>;
}